import { makeRequest, DATA_URL_IMAGE_REGEX, uploadImageDataUrlAndReturnUrl } from "./client";

export const advertisementsApi = {
  getPricingConfig: async (): Promise<{
    pricing: {
      pricingTiers: Array<{ maxActiveUsers: number; basePrice: number }>;
      typeMultipliers: Record<string, number>;
      durationMultipliers: {
        upTo7Days: number;
        upTo14Days: number;
        over14Days: number;
        defaultDays: number;
      };
    };
  }> => {
    return makeRequest("/v1/advertisements/pricing-config", {
      method: "GET",
    });
  },
  create: async (advertisement: {
    photos: string[];
    bannerImage?: string;
    title: string;
    price: string;
    category: string;
    description: string;
    location: string;
    contactName?: string;
    contactNumber?: string;
    contactEmail?: string;
    adType?: "standard" | "featured" | "banner" | "homepage";
    hideTextOverlay?: boolean;
    isDraft?: boolean;
    expiresAt?: string;
    activeUsers?: number;
  }, token: string): Promise<{
    message: string;
    advertisement: {
      advertisementId: string;
      organizationId: string;
      photos: string[];
      bannerImage?: string;
      title: string;
      price: string;
      category: string;
      description: string;
      location: string;
      contactName?: string;
      contactNumber?: string;
      contactEmail?: string;
      adType: "standard" | "featured" | "banner" | "homepage";
      isDraft?: boolean;
      createdAt: string;
      updatedAt: string;
      expiresAt?: string;
      createdBy: string;
    };
  }> => {
    const uploadedPhotos = await Promise.all(
      (advertisement.photos || []).map(async (photo, index) => {
        if (typeof photo === "string" && DATA_URL_IMAGE_REGEX.test(photo)) {
          return uploadImageDataUrlAndReturnUrl(
            photo,
            token,
            "advertisements",
            `ad-photo-${index + 1}`
          );
        }
        return photo;
      })
    );

    let uploadedBannerImage = advertisement.bannerImage;
    if (advertisement.bannerImage && typeof advertisement.bannerImage === "string" && DATA_URL_IMAGE_REGEX.test(advertisement.bannerImage)) {
      uploadedBannerImage = await uploadImageDataUrlAndReturnUrl(
        advertisement.bannerImage,
        token,
        "advertisements",
        "ad-banner-image"
      );
    }

    return makeRequest("/v1/advertisements", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        ...advertisement,
        contactEmail: advertisement.contactEmail?.trim() || undefined,
        photos: uploadedPhotos,
        bannerImage: uploadedBannerImage,
      }),
    });
  },

  getAll: async (token: string, organizations?: string[], includeDeleted?: boolean): Promise<{
    advertisements: Array<{
      advertisementId: string;
      organizationId: string;
      photos: string[];
      bannerImage?: string;
      title: string;
      price: string;
      category: string;
      description: string;
      location: string;
      contactName?: string;
      contactNumber?: string;
      contactEmail?: string;
      adType: "standard" | "featured" | "banner" | "homepage";
      isDraft?: boolean;
      createdAt: string;
      updatedAt: string;
      expiresAt?: string;
      createdBy: string;
      isDeleted?: boolean;
      deletedAt?: string;
      deletedBy?: string;
      advertisementFee?: number;
      organizationRevenue?: number;
      platformRevenue?: number;
    }>;
    count: number;
  }> => {
    let url = organizations && organizations.length > 0
      ? `/v1/advertisements?organizations=${organizations.join(",")}`
      : "/v1/advertisements";
    if (includeDeleted) {
      url += url.includes("?") ? "&includeDeleted=true" : "?includeDeleted=true";
    }
    return makeRequest(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  get: async (advertisementId: string, token: string): Promise<{
    advertisement: {
      advertisementId: string;
      organizationId: string;
      photos: string[];
      bannerImage?: string;
      title: string;
      price: string;
      category: string;
      description: string;
      location: string;
      contactName?: string;
      contactNumber?: string;
      contactEmail?: string;
      adType: "standard" | "featured" | "banner" | "homepage";
      isDraft?: boolean;
      createdAt: string;
      updatedAt: string;
      expiresAt?: string;
      createdBy: string;
    };
  }> => {
    return makeRequest(`/v1/advertisements/${advertisementId}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  update: async (
    advertisementId: string,
    advertisement: Partial<{
      photos: string[];
      bannerImage?: string;
      title: string;
      price: string;
      category: string;
      description: string;
      location: string;
      contactName?: string;
      contactNumber?: string;
      contactEmail?: string;
      adType: "standard" | "featured" | "banner" | "homepage";
      hideTextOverlay?: boolean;
      isDraft?: boolean;
      expiresAt?: string;
    }>,
    token: string
  ): Promise<{
    message: string;
    advertisement: {
      advertisementId: string;
      organizationId: string;
      photos: string[];
      bannerImage?: string;
      title: string;
      price: string;
      category: string;
      description: string;
      location: string;
      contactName?: string;
      contactNumber?: string;
      contactEmail?: string;
      adType: "standard" | "featured" | "banner" | "homepage";
      isDraft?: boolean;
      createdAt: string;
      updatedAt: string;
      expiresAt?: string;
      createdBy: string;
    };
  }> => {
    let updatedPayload = advertisement;

    if (advertisement.contactEmail !== undefined) {
      updatedPayload = {
        ...updatedPayload,
        contactEmail: advertisement.contactEmail?.trim() || undefined,
      };
    }

    if (advertisement.photos) {
      const uploadedPhotos = await Promise.all(
        advertisement.photos.map(async (photo, index) => {
          if (typeof photo === "string" && DATA_URL_IMAGE_REGEX.test(photo)) {
            return uploadImageDataUrlAndReturnUrl(
              photo,
              token,
              advertisementId,
              `ad-photo-${index + 1}`
            );
          }
          return photo;
        })
      );
      updatedPayload = { ...updatedPayload, photos: uploadedPhotos };
    }

    if (advertisement.bannerImage && typeof advertisement.bannerImage === "string" && DATA_URL_IMAGE_REGEX.test(advertisement.bannerImage)) {
      const uploadedBannerImage = await uploadImageDataUrlAndReturnUrl(
        advertisement.bannerImage,
        token,
        advertisementId,
        "ad-banner-image"
      );
      updatedPayload = { ...updatedPayload, bannerImage: uploadedBannerImage };
    }

    return makeRequest(`/v1/advertisements/${advertisementId}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(updatedPayload),
    });
  },

  delete: async (advertisementId: string, token: string): Promise<{
    message: string;
    isBillable?: boolean;
  }> => {
    return makeRequest(`/v1/advertisements/${advertisementId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  sendReceipt: async (advertisementId: string, email?: string, token?: string): Promise<{
    message: string;
    email: string;
  }> => {
    if (!token) {
      throw new Error("Token is required");
    }
    return makeRequest(`/v1/advertisements/${advertisementId}/send-receipt`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ email }),
    });
  },
};
